import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Loader2, Layers, CalendarClock, Puzzle, PenLine, CheckCircle2, ChevronRight, Printer } from "lucide-react";
import DocHeader from "@/components/DocHeader";
import ContinuumTriangle from "@/components/ContinuumTriangle";

const LEVELS = [
  { key: "classroom", label: "Classroom Support", tint: "#81B29A" },
  { key: "school", label: "School Support", tint: "#6C8EBF" },
  { key: "school_plus", label: "School Support Plus", tint: "#C0698D" },
];
const FLAG = {
  overdue: { text: "Overdue", cls: "bg-red-50 text-red-600" },
  due_soon: { text: "Due soon", cls: "bg-amber-50 text-amber-600" },
  scheduled: { text: "Scheduled", cls: "bg-emerald-50 text-emerald-600" },
};
const SIG = {
  signed: { text: "Signed", cls: "bg-emerald-50 text-emerald-600", Icon: CheckCircle2 },
  awaiting: { text: "Awaiting", cls: "bg-amber-50 text-amber-600", Icon: PenLine },
};

export default function ProvisionMap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/provision-map").then((r) => setData(r.data)).catch(() => navigate("/"));
  }, [navigate]);

  if (!data) return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const { rows, counts, reviews_due, totals } = data;
  const onLevel = LEVELS.reduce((n, l) => n + (counts[l.key] || 0), 0);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <div className="max-w-5xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between gap-3 mb-5 no-print">
          <button data-testid="provision-back-btn" onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft size={16} /> Back to dashboard
          </button>
          <button data-testid="provision-print-btn" onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold font-['Outfit'] text-white transition hover:opacity-90"
            style={{ background: "#44546A" }}>
            <Printer size={16} /> Print / Export
          </button>
        </div>
        <div className="print-area">
        <DocHeader printOnly subtitle="Whole-School Provision Map" />
        <div className="flex items-center gap-2.5 mb-1">
          <span className="h-9 w-9 rounded-xl bg-[#7FB069] flex items-center justify-center text-white"><Layers size={18} /></span>
          <h1 className="font-['Outfit'] text-3xl font-bold text-slate-800">Whole-School Provision Map</h1>
        </div>
        <p className="text-slate-500 mb-7">Every pupil on the Continuum of Support across all your classes.</p>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="provision-stats">
          <Stat label="On the continuum" value={onLevel} sub={`of ${totals.students} pupils`} tint="#7FB069" />
          <Stat label="Reviews due" value={reviews_due.length} sub="overdue / soon" tint="#C0698D" />
          <Stat label="Awaiting signature" value={totals.awaiting_signature} sub="parent sign-off" tint="#E0A33E" />
          <Stat label="Signed plans" value={totals.signed} sub="complete" tint="#6C8EBF" />
        </div>

        {/* Level breakdown */}
        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          {LEVELS.map((l) => (
            <div key={l.key} className="bg-white rounded-2xl border border-slate-100 p-4" style={{ borderTop: `3px solid ${l.tint}` }} data-testid={`provision-level-${l.key}`}>
              <p className="text-sm text-slate-500">{l.label}</p>
              <p className="font-['Outfit'] text-3xl font-bold" style={{ color: l.tint }}>{counts[l.key] || 0}</p>
            </div>
          ))}
        </div>

        {/* Continuum triangle legend with live pupil counts per tier */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-5 mb-6" data-testid="provision-triangle">
          <ContinuumTriangle counts={counts} />
        </div>

        {/* Reviews due */}
        {reviews_due.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-5 mb-6" data-testid="provision-reviews-due">
            <p className="font-semibold font-['Outfit'] text-slate-800 flex items-center gap-1.5 mb-3"><CalendarClock size={17} className="text-[#C0698D]" /> Reviews due</p>
            <div className="space-y-2">
              {reviews_due.map((r) => {
                const f = FLAG[r.review_flag];
                return (
                  <button key={r.id} data-testid={`provision-due-${r.id}`} onClick={() => navigate(`/student/${r.id}/cos`)}
                    className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3.5 py-2.5 hover:border-[#7FB069] transition text-left">
                    <span className="font-semibold text-slate-700 text-sm truncate">{r.name} <span className="text-slate-400 font-normal">· {r.class_name}</span></span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-slate-400">{r.review_date}</span>
                      {f && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${f.cls}`}>{f.text}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* All pupils grouped by level */}
        <div className="space-y-5">
          {LEVELS.map((l) => {
            const list = rows.filter((r) => r.level === l.key);
            if (list.length === 0) return null;
            return (
              <div key={l.key}>
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: l.tint }}>{l.label} <span className="text-slate-300">({list.length})</span></p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {list.map((r) => <Row key={r.id} r={r} onClick={() => navigate(`/student/${r.id}/cos`)} />)}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tint }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4">
      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">{label}</p>
      <p className="font-['Outfit'] text-3xl font-bold mt-1" style={{ color: tint }}>{value}</p>
      <p className="text-xs text-slate-400">{sub}</p>
    </div>
  );
}

function Row({ r, onClick }) {
  const f = FLAG[r.review_flag];
  const sig = SIG[r.signature];
  return (
    <button data-testid={`provision-student-${r.id}`} onClick={onClick}
      className="w-full text-left rounded-xl border border-slate-100 bg-white p-3 hover:border-[#7FB069] hover:shadow-sm transition group">
      <div className="flex items-center justify-between">
        <span className="font-semibold font-['Outfit'] text-slate-800 flex items-center gap-1.5 truncate">
          {r.name}{r.is_asd && <Puzzle size={13} className="text-[#3E9B96] shrink-0" />}
        </span>
        <ChevronRight size={16} className="text-slate-300 group-hover:text-[#7FB069] shrink-0" />
      </div>
      <p className="text-xs text-slate-400 mt-0.5">{r.class_name}</p>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        {f && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${f.cls}`}>{f.text}</span>}
        {sig && <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${sig.cls}`}><sig.Icon size={11} /> {sig.text}</span>}
      </div>
    </button>
  );
}
